import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, message, Row, Select, Typography, Upload } from "antd";
import React, { useState } from "react";
import Papa from "papaparse";

const PROGRAM_DAYS_OPTIONS = [
    { label: "1 day", value: 1 },
    { label: "5 days", value: 5 },
    { label: "6 days", value: 6 },
];
 
const SyndicationReportExporter: React.FC = () => {
    const { Title } = Typography;
    const [fileList, setFileList] = useState<any[]>([]);
    const [programDays, setProgramDays] = useState<number | null>(null);
 
    const handleBeforeUpload = (file: File) => {
        const isValidFile =
            file.type === "text/csv" ||
            file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.type === "application/vnd.ms-excel" ||
            file.name.endsWith(".csv") ||
            file.name.endsWith(".xlsx") ||
            file.name.endsWith(".xls");
 
        if (!isValidFile) {
            message.error("Only CSV or Excel (.xlsx, .xls) files are allowed!");
            return Upload.LIST_IGNORE;
        }
 
        setFileList([
            {
                originFileObj: file,
                name: file.name,
                status: "done",
            },
        ]);
 
        return false;
    };
 
    const handleOnChange = (info: any) => {
        setFileList(info.fileList);
    };

    const getAdvMaxPerDay = (total: number, days: number): string => {
        return String(Math.ceil(total / days));
    };
 
    const handleUpload = async () => {
        if (!fileList.length) {
            message.error("No file selected");
            return;
        }

        if (!programDays) {
            message.error("Please select the number of program days");
            return;
        }
 
        const file = fileList[0].originFileObj;
 
        if (!file) {
            message.error("Invalid file");
            return;
        }
 
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
 
            complete: results => {
                const rows = results.data as any[];
 
                const advertiserTotals: Record<string, number> = {};
                const brandTotals: Record<string, number> = {};
 
                // Step 1: Build totals using Units Available
                rows.forEach(row => {
                    const advertiser = row["Advertiser Name"]?.trim() || "";
                    const brand = row["Brand"]?.trim() || "";
                    const unitsRemaining = Number(row["Units Remaining"] || 0);
                    advertiserTotals[advertiser] = (advertiserTotals[advertiser] || 0) + unitsRemaining;
                    brandTotals[brand] = (brandTotals[brand] || 0) + unitsRemaining;
                });
                
                // Step 2: Update rows
                const updatedData = rows.map(row => {
                    const advertiser = row["Advertiser Name"]?.trim() || "";
                    const brand = row["Brand"]?.trim() || "";
                    const existingMaxPerDay = row["Max Per Day"]?.trim() || "";
 
                    const advTotal = advertiserTotals[advertiser] || 0;
                    const brandTotal = brandTotals[brand] || 0;
 
                    const advMaxPerDay = getAdvMaxPerDay(advTotal, programDays);
                    const maxPerDayCalc = existingMaxPerDay ? existingMaxPerDay : String(Math.ceil(brandTotal / programDays));
                    const effectiveAdvMaxPerDay = existingMaxPerDay ? existingMaxPerDay : advMaxPerDay;
 
                    const newRow: any = {};
 
                    Object.keys(row).forEach(key => {
                        if (key === "Max Per Day") {
                            newRow[key] = row[key];
                        } else if (key === "Max Per Day Calc") {
                            newRow[key] = maxPerDayCalc;
                            newRow["Adv Max Per Day"] = effectiveAdvMaxPerDay;
                        } else {
                            newRow[key] = row[key];
                        }
                    });
 
                    return newRow;
                });
 
                const csv = Papa.unparse(updatedData);
 
                const blob = new Blob([csv], {
                    type: "text/csv;charset=utf-8;",
                });
 
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
 
                link.href = url;
                link.download = file.name;
                link.click();
 
                URL.revokeObjectURL(url);
 
                message.success("CSV downloaded successfully");
            },
 
            error: () => {
                message.error("Failed to read CSV");
            },
        });
    };

    const isReady = fileList.length > 0 && programDays !== null;
 
    return (
        <Card
            title={<Title level={4}>Upload your report</Title>}
            style={{ maxWidth: 900, margin: "20px auto", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
            bordered={true}
        >
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={24}>
                    <Typography.Text strong>Program Days:</Typography.Text>
                    <Select
                        placeholder="Select program days..."
                        size="large"
                        options={PROGRAM_DAYS_OPTIONS}
                        onChange={(val) => setProgramDays(val)}
                        value={programDays}
                        style={{ width: "100%", marginTop: 4 }}
                    />
                </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={24}>
                    <Upload
                        beforeUpload={handleBeforeUpload}
                        onChange={handleOnChange}
                        fileList={fileList}
                        maxCount={1}
                        onRemove={() => setFileList([])}
                    >
                        <Button icon={<UploadOutlined />} size="large">
                            Upload File
                        </Button>
                    </Upload>
                </Col>
            </Row>
            {fileList.length > 0 && (
                <Row style={{ marginTop: 16 }}>
                    <Col span={24}>
                        <Alert
                            message="File Selected"
                            description={`File "${fileList[0].name}" is ready to upload.`}
                            type="info"
                            showIcon
                        />
                    </Col>
                </Row>
            )}
 
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={24}>
                    <Button
                        type="primary"
                        onClick={handleUpload}
                        disabled={!isReady}
                        size="large"
                        block
                    >
                        Download
                    </Button>
                </Col>
            </Row>
        </Card>
    );
};
 
export default SyndicationReportExporter;
